// Typed config (plan §17; brief invariant 6): every tunable lives here, engine code contains
// no magic numbers, tests assert SYMBOLICALLY against these values. Frozen at game start,
// delivered as canonical JSON + hash inside Welcome (plan §17.6), embedded in the log header
// (plan §11.2). Seed doctrine (quarry config-simConfig.ts): typed frozen sections, one merged
// DEFAULT, inline-documented values.

/** Rules constants (plan §17.1; outline citations inline at the defaults). */
export interface EngineConfig {
  readonly victoryTarget: number;
  readonly vp: { readonly settlement: number; readonly city: number; readonly landmark: number; readonly award: number };
  readonly piecePools: { readonly road: number; readonly settlement: number; readonly city: number };
  readonly discardOver: number; // hands STRICTLY over this discard ⌊n/2⌋ on a 7 (outline §9)
  /** Friendly Bandit: a seat with PUBLIC vp ≤ this cannot be robbed. ≤ 0 disables it (classic). */
  readonly friendlyRobberVp: number;
  readonly routeMin: number; // Longest Route claim threshold (outline §11)
  readonly musterMin: number; // Muster claim threshold (outline §11)
  readonly advancePerTurn: number; // cards playable per turn (outline §10)
  readonly harborRates: { readonly base: number; readonly generic: number; readonly specific: number };
  /** disc number → pip count (outline §2); red numbers get the warning tint. */
  readonly discPips: Readonly<Record<number, number>>;
  readonly redNumbers: readonly number[];
  /** Setup-ordering letter → disc number (outline §2, canonical A–R map). */
  readonly spiralLetters: Readonly<Record<string, number>>;
  readonly keyframePeriod: number; // log keyframe cadence in actions (plan §11.2)
  readonly offersPerSeatCap: number; // open trade offers per seat (plan §8.2)
  readonly mapGenRejectBound: number; // red-spacing rejection bound (plan §9.2 R5)
  readonly maxHexes: number; // map schema cap (plan §6.7)
}

/**
 * Player-count scaling (plan §6.6, outline §13.4): band b = max(0, ⌈(N−4)/2⌉); every table
 * is base + delta·b — the official 5–6 extension's actual ratios, extrapolated for b=2 (D12).
 */
export interface ScalingConfig {
  readonly supplyStockBase: number; // 19 per resource (outline §2)
  readonly supplyStockPerBand: number; // +5 (outline §13.4)
  readonly landHexesBase: number; // 19
  readonly landHexesPerBand: number; // +11 (outline §13.1)
  readonly desertsBase: number; // 1
  readonly desertsPerBand: number; // +1
  /** terrain tile counts by tile id: base and per-band delta (outline §2/§13.1). */
  readonly terrainBase: Readonly<Record<string, number>>;
  readonly terrainPerBand: Readonly<Record<string, number>>;
  /** disc copies: the {2,12} ends and the eight mid numbers (outline §2/§13.1). */
  readonly discEndsBase: number; // 1 each of 2 and 12
  readonly discEndsPerBand: number; // +1
  readonly discMidsBase: number; // 2 each of 3,4,5,6,8,9,10,11
  readonly discMidsPerBand: number; // +1
  /** advance-deck composition per band (outline §2/§13.1; b=2 extrapolated, D12). */
  readonly deckBase: Readonly<Record<string, number>>;
  readonly deckPerBand: Readonly<Record<string, number>>;
  readonly harborsGenericBase: number; // 4 × 3:1
  readonly harborsGenericPerBand: number; // +1
  /** specific 2:1 harbors: one per resource at base; band additions in resource order (D12). */
  readonly harborSpecificAdditions: readonly string[]; // b≥1 → [0], b≥2 → [0]+[1], …
}

export interface GameConfigShape {
  readonly engine: EngineConfig;
  readonly scaling: ScalingConfig;
}

/** Scaling band for N seats (plan §6.6): N≤4 ⇒ 0; 5–6 ⇒ 1; 7–8 ⇒ 2. */
export function scalingBand(seats: number): number {
  return Math.max(0, Math.ceil((seats - 4) / 2));
}

/** The concrete per-band counts the generator and its tests read (plan §6.6 table). */
export interface ScaledCounts {
  landHexes: number;
  deserts: number;
  producing: number;
  terrain: Record<string, number>;
  discs: Record<number, number>;
  discTotal: number;
  supplyStockPerResource: number;
  deck: Record<string, number>;
  deckTotal: number;
  harborsGeneric: number;
  harborsSpecific: string[]; // resource ids, one 2:1 each (base: every resource once + additions)
  harborsTotal: number;
}

export function scaledCounts(cfg: GameConfigShape, resourceOrder: readonly string[], seats: number): ScaledCounts {
  const s = cfg.scaling;
  const b = scalingBand(seats);
  const landHexes = s.landHexesBase + s.landHexesPerBand * b;
  const deserts = s.desertsBase + s.desertsPerBand * b;
  const terrain: Record<string, number> = {};
  for (const t of Object.keys(s.terrainBase)) terrain[t] = (s.terrainBase[t] ?? 0) + (s.terrainPerBand[t] ?? 0) * b;
  terrain['desert'] = deserts;
  const discs: Record<number, number> = {};
  for (const n of [2, 12]) discs[n] = s.discEndsBase + s.discEndsPerBand * b;
  for (const n of [3, 4, 5, 6, 8, 9, 10, 11]) discs[n] = s.discMidsBase + s.discMidsPerBand * b;
  const discTotal = Object.values(discs).reduce((a, v) => a + v, 0);
  const deck: Record<string, number> = {};
  for (const c of Object.keys(s.deckBase)) deck[c] = (s.deckBase[c] ?? 0) + (s.deckPerBand[c] ?? 0) * b;
  const deckTotal = Object.values(deck).reduce((a, v) => a + v, 0);
  const harborsSpecific = [...resourceOrder, ...s.harborSpecificAdditions.slice(0, b)];
  const harborsGeneric = s.harborsGenericBase + s.harborsGenericPerBand * b;
  return {
    landHexes,
    deserts,
    producing: landHexes - deserts,
    terrain,
    discs,
    discTotal,
    supplyStockPerResource: s.supplyStockBase + s.supplyStockPerBand * b,
    deck,
    deckTotal,
    harborsGeneric,
    harborsSpecific,
    harborsTotal: harborsGeneric + harborsSpecific.length,
  };
}
